import { Typography } from '@snack-uikit/typography';
import { useRouter } from 'next/navigation';
import { memo, useCallback } from 'react';
import { useAnalytics } from 'src/hooks/useAnalytics';
import { type Catalog, CatalogProduct } from 'src/types/backendContent';

import { type CardClickHandler, CardProduct } from '../CardProduct';
import s from './ProductsList.module.scss';

type CatalogSectionProps = {
  title: string;
  products: Catalog['products'];
  searchedPhrase: string;
  startIndex: number;
};

const MemoizedCard = memo(CardProduct);

const renderProduct =
  (onClick: CardClickHandler) =>
  // eslint-disable-next-line react/display-name
  (product: CatalogProduct, index: number) => {
    const href = product.customLink
      ? `/${product.customLink}`
      : product.type === 'solutions'
      ? `/solutions/${product.slug}`
      : `/products/${product.slug}`;
    return (
      <MemoizedCard
        key={product.id}
        onClick={onClick}
        href={href}
        productNumber={index}
        {...product}
      />
    );
  };

export function CatalogSection({
  title,
  products,
  searchedPhrase,
  startIndex,
}: CatalogSectionProps) {
  const { clickAnalytics, searchClickAnalytics, searchResultClickAnalytics } =
    useAnalytics();
  const router = useRouter();

  const handleClick: CardClickHandler = useCallback(
    ({ e, title, href, source, requestSearchId, positionNumber }) => {
      e.preventDefault();

      if (searchedPhrase.length > 0) {
        searchClickAnalytics({
          link: href,
          searchPhrase: searchedPhrase,
          type: 'page-all-products',
          source: source,
        });
        searchResultClickAnalytics({
          link: href,
          searchPhrase: searchedPhrase,
          type: 'live-page-products',
          source: source,
          category: 'site-body',
          requestId: requestSearchId ?? 'placeholder',
          positionNumber:
            positionNumber !== undefined
              ? String(startIndex + positionNumber + 1)
              : undefined,
        });
      } else {
        clickAnalytics({
          action: href,
          clickZone: 'body',
          clickElement: 'cardclick',
          clickContent: title,
          uniqueId: `products-catalog-card-click-${title}`,
          transitionType: 'inside-link',
          clickZoneCategory: 'site-body',
          transitionUrl: href,
          transitionTypeMetrics: 'internal',
        });
      }

      router.push(href);
    },
    [
      clickAnalytics,
      router,
      searchClickAnalytics,
      searchResultClickAnalytics,
      searchedPhrase,
    ],
  );

  return (
    <div>
      <Typography.SansTitleM className={s.sectionTitle} tag="h2">
        {title}
      </Typography.SansTitleM>
      <div className={s.sectionProducts}>
        {products.map(renderProduct(handleClick))}
      </div>
    </div>
  );
}
